<template>
  <div>
    <MultiTextSearchFacet
      :entityType="'Project'"
      :title="'Project ID'"
      :fieldName="'projectIds'"
      :placeholderText="'Enter Project ID'"
    />
    <SearchFacetOptions
      :entityType="'Project'"
      :options="projectStatusOptions"
      :title="'Status'"
      :fieldName="'projectStatuses'"
      :loading="projectStatusOptionsLoading"
    />
    <SearchFacetOptions
      :entityType="'Project'"
      :options="agreementTypeOptions"
      :title="'Active Agreement Type'"
      :fieldName="'agreementTypes'"
      :loading="agreementTypeOptionsLoading"
    />
    <SearchFacetOptions
      :entityType="'Project'"
      :options="localityOptions"
      :title="'Locality'"
      :fieldName="'localities'"
      :loading="localityLoading"
    />
    <SearchFacetOptions
      :entityType="'Project'"
      :options="partnershipGrantSearchOptions"
      :title="'Partnership Grant'"
      :fieldName="'partnershipGrants'"
      :loading="partnershipGrantSearchOptionsLoading"
      :onlyOneAllowed="false"
    />
    <div class="row search-filter-category">
      <div class="col-12">
        <a v-b-toggle="`collapse-executed-date-facet`" class="facet-collapse-icon c-pointer">
          <font-awesome-icon
            class="when-opened "
            icon='caret-down'
            size="lg" />
          <font-awesome-icon
            class="when-closed fa-w-10"
            icon='caret-right'
            size="lg" />
        </a>
        <span class="font-weight-bold ">Executed Event Date</span>
        <b-collapse :id="`collapse-executed-date-facet`"
          class="row"
          :visible="initialSearchCategoryStates['executedDates']">
          <div class="search-panel-items search-panel-no-border search-filter-list">
            <div class="search-panel-sub-item">
              <span class="sub-search-type-filter search-panel-items">With Date After</span>
              <datepicker
                id="executedDateAfter"
                :class="'flex-grow-1'"
                format="MM/dd/yyyy"
                input-class="form-control bg-white"
                :clear-button="true"
                v-model="executedDateAfter"
                :bootstrap-styling="true"
                name="executedDateAfter"
              >
                <div slot="afterDateInput" class="input-group-append">
                  <span class="input-group-text">
                    <font-awesome-icon icon="calendar-alt" />
                  </span>
                </div>
              </datepicker>
            </div>
            <span class="sub-search-type-filter search-panel-items">With Date Before</span>
            <div class="search-panel-sub-item">
              <datepicker
                id="executedDateBefore"
                :class="'flex-grow-1'"
                format="MM/dd/yyyy"
                input-class="form-control bg-white"
                :clear-button="true"
                v-model="executedDateBefore"
                :bootstrap-styling="true"
                name="executedDateBefore"
              >
                <div slot="afterDateInput" class="input-group-append">
                  <span class="input-group-text">
                    <font-awesome-icon icon="calendar-alt" />
                  </span>
                </div>
              </datepicker>
            </div>
          </div>
        </b-collapse>
      </div>
    </div>
    <div class="row search-filter-category">
      <div class="col-12">
        <a v-b-toggle="`collapse-total-acres-facet`" class="facet-collapse-icon c-pointer">
          <font-awesome-icon
            class="when-opened "
            icon='caret-down'
            size="lg" />
          <font-awesome-icon
            class="when-closed fa-w-10"
            icon='caret-right'
            size="lg" />
        </a>
        <span class="font-weight-bold ">Total Acres</span>
        <b-collapse :id="`collapse-total-acres-facet`"
          class="row"
          :visible="initialSearchCategoryStates['acres']">
          <div class="search-panel-items search-panel-no-border search-filter-list">
            <div class="search-panel-sub-item">
              <span class="sub-search-type-filter">Minimum Acres</span>
              <input class="form-control text-search-entry"
                v-validate="'max_decimal:4'"
                placeholder="Enter Minimum Acres"
                name="minimumAcres"
                data-vv-as="Minimum Acres"
                v-model="minimumAcres"/>
              <div class="invalid-feedback">{{ errors.first('minimumAcres') }}</div>
            </div>
            <div class="search-panel-sub-item">
              <span class="sub-search-type-filter">Maximum Acres</span>
              <input class="form-control text-search-entry"
                v-validate="'max_decimal:4'"
                placeholder="Enter Maximum Acres"
                name="maximumAcres"
                data-vv-as="Maximum Acres"
                v-model="maximumAcres"/>
              <div class="invalid-feedback">{{ errors.first('maximumAcres') }}</div>
            </div>
          </div>
        </b-collapse>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { LookupModules } from '@/store/moduleConfig';
import moment from 'moment-timezone';
import Datepicker from 'vuejs-datepicker';
import MultiTextSearchFacet from '@/components/search/MultiTextSearchFacet.vue';
import SearchFacetOptions from '@/components/search/SearchFacetOptions.vue';

export default {
  name: 'PropertySearchFacets',
  components: {
    Datepicker,
    MultiTextSearchFacet,
    SearchFacetOptions,
  },
  computed: {
    initialSearchCategoryStates() {
      return this.$store.state.Search.initialSearchCategoryStates;
    },
    ...mapState(LookupModules.LOCALITIES.namespace, {
      localityLoading: state => state.loading,
    }),
    ...mapState(LookupModules.AGREEMENT_TYPES.namespace, {
      agreementTypeOptionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.PROJECT_STATUSES.namespace, {
      projectStatusOptionsLoading: state => state.loading,
    }),
    ...mapState(LookupModules.PARTNERSHIP_GRANT_SEARCH_OPTIONS.namespace, {
      partnershipGrantSearchOptionsLoading: state => state.loading,
    }),
    ...mapGetters(LookupModules.LOCALITIES.namespace, {
      localityOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.AGREEMENT_TYPES.namespace, {
      agreementTypeOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.PROJECT_STATUSES.namespace, {
      projectStatusOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.PARTNERSHIP_GRANT_SEARCH_OPTIONS.namespace, {
      partnershipGrantSearchOptions: 'getActiveOptions',
    }),
    executedDateAfter: {
      get() {
        return this.$store.state.Search.selectedSearchFacets.executedDateAfter[0];
      },
      set(value) {
        this.setDateFacet('executedDateAfter', value);
      },
    },
    executedDateBefore: {
      get() {
        return this.$store.state.Search.selectedSearchFacets.executedDateBefore[0];
      },
      set(value) {
        this.setDateFacet('executedDateBefore', value);
      },
    },
    minimumAcres: {
      get() {
        return this.$store.state.Search.selectedSearchFacets.minimumAcres[0];
      },
      set(value) {
        this.setFacet('minimumAcres', value);
      },
    },
    maximumAcres: {
      get() {
        return this.$store.state.Search.selectedSearchFacets.maximumAcres[0];
      },
      set(value) {
        this.setFacet('maximumAcres', value);
      },
    },
  },
  methods: {
    setDateFacet(fieldName, value) {
      this.setFacet(fieldName,
        [moment.tz(value, 'UTC').tz('America/New_York').format('MM/DD/YYYY')]);
    },
    setFacet(fieldName, value) {
      this.$store.dispatch(
        'Search/updateSearchFacet',
        {
          type: fieldName,
          values: value,
        },
      );
    },
  },
  mounted() {
    this.$store.dispatch(`${LookupModules.LOCALITIES.namespace}/fetchOptions`,
      LookupModules.LOCALITIES.url);
    this.$store.dispatch(`${LookupModules.AGREEMENT_TYPES.namespace}/fetchOptions`,
      LookupModules.AGREEMENT_TYPES.url);
    this.$store.dispatch(`${LookupModules.PROJECT_STATUSES.namespace}/fetchOptions`,
      LookupModules.PROJECT_STATUSES.url);
    this.$store.dispatch(`${LookupModules.PARTNERSHIP_GRANT_SEARCH_OPTIONS.namespace}/fetchOptions`,
      LookupModules.PARTNERSHIP_GRANT_SEARCH_OPTIONS.url);
  },
};
</script>
